import { useMemo } from "react"
import {
  createFileRoute,
  Link,
  useNavigate,
  useParams,
} from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import {
  AlertTriangle,
  CalendarClock,
  MapPin,
  Send,
  Users,
} from "lucide-react"
import { useActiveEvent, useEvents } from "@/core/active-event"
import {
  guestName,
  guestStats,
  isTicketOpen,
  overviewAgendaQueryOptions,
  overviewGuestsQueryOptions,
  overviewTicketsQueryOptions,
  publishEvent,
  ticketPriorityRank,
} from "@/core/overview"
import { Avatar } from "@/components/ui/Avatar"
import { OverviewReports } from "@/components/overview/OverviewReports"
import { ActivityColumn } from "@/components/overview/ActivityColumn"
import { FunnelHero, TicketsStatCard } from "@/components/overview/OverviewCharts"
import { getSessionUser } from "@/core/auth"
import { ticketGuestName } from "@/core/tickets"

export const Route = createFileRoute("/$orgSlug/$eventSlug/")({
  component: OverviewView,
})

// Terminal/inactive event states that still warrant a status chip in the hero.
// (Published/active is the default and shows nothing.)
const INACTIVE_STATUSES = new Set(["cancelled", "canceled", "completed", "archived", "expired"])

function OverviewView() {
  const { eventId, row } = useActiveEvent()
  const params = useParams({ from: "/$orgSlug/$eventSlug/" })
  const { orgSlug, eventSlug } = params
  const navigate = useNavigate()
  const qc = useQueryClient()
  const reloadEvents = useEvents((s) => s.load)

  const guestsQ = useQuery({ ...overviewGuestsQueryOptions(eventId ?? ""), enabled: !!eventId })
  const ticketsQ = useQuery({ ...overviewTicketsQueryOptions(eventId ?? ""), enabled: !!eventId })
  const agendaQ = useQuery({ ...overviewAgendaQueryOptions(eventId ?? ""), enabled: !!eventId })

  const guests = useMemo(() => guestsQ.data ?? [], [guestsQ.data])
  const tickets = useMemo(() => ticketsQ.data ?? [], [ticketsQ.data])
  const agenda = useMemo(() => agendaQ.data ?? [], [agendaQ.data])

  const stats = useMemo(() => guestStats(guests), [guests])
  const openTickets = useMemo(() => tickets.filter(isTicketOpen), [tickets])
  // "Mine" = open and assigned to me. Unassigned tickets belong to nobody, so
  // they don't count here — they're in the total, waiting to be picked up.
  const myUserId = getSessionUser()?.id ?? null
  const myOpenTickets = useMemo(
    () => (myUserId ? openTickets.filter((t) => t.assigneeUserId === myUserId).length : 0),
    [openTickets, myUserId],
  )

  const guestNameById = useMemo(() => {
    const m = new Map<string, string>()
    // Keyed by guest id AND linked account id — a ticket's guest side may be either.
    for (const g of guests) {
      m.set(g.id, guestName(g))
      if (g.linkedUserId) m.set(g.linkedUserId, guestName(g))
    }
    return m
  }, [guests])

  // Highest-signal open tickets first (urgent → high → …), then most recent.
  const needsAttention = useMemo(
    () =>
      [...openTickets]
        .sort((a, b) => {
          const r = ticketPriorityRank(a.priority) - ticketPriorityRank(b.priority)
          if (r !== 0) return r
          return (b.modifiedAt ?? b.createdAt).localeCompare(a.modifiedAt ?? a.createdAt)
        })
        .slice(0, 4),
    [openTickets],
  )

  const upNext = useMemo(() => agenda.slice(0, 3), [agenda])

  const publish = useMutation({
    mutationFn: () => publishEvent(eventId!),
    onSuccess: (data) => {
      toast.success("Event published — invites can now be sent")
      void reloadEvents()
      // Splice the new status onto cached event rows where the realtime layer
      // would; here a simple refetch of the events list is enough.
      if (data?.status) void qc.invalidateQueries({ queryKey: ["resource", "events"] })
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Couldn't publish"),
  })

  const initials = useMemo(() => deriveInitials(row?.name ?? ""), [row?.name])
  const status = row?.status ?? "draft"

  // The event hero IS the page header here (no separate "Overview" bar). Events
  // default to published/active, so the common case shows no status chip at all:
  // a draft still gets a publish CTA, and only terminal states (cancelled /
  // completed / archived / expired) surface a muted pill.
  const statusControl =
    status === "draft" ? (
      <button
        type="button"
        onClick={() => publish.mutate()}
        disabled={publish.isPending}
        className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg bg-[var(--accent)] px-3 text-[12.5px] font-semibold text-white shadow-fluent-sm transition hover:opacity-90 disabled:opacity-60"
      >
        <Send className="h-3.5 w-3.5" />
        {publish.isPending ? "Publishing…" : "Publish event"}
      </button>
    ) : INACTIVE_STATUSES.has(status) ? (
      <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-plum-100 px-2.5 py-1 text-[11px] font-semibold text-plum-700/70 dark:bg-white/[0.08] dark:text-plum-200/55">
        {cap(status)}
      </span>
    ) : null

  if (!eventId || !orgSlug || !eventSlug) {
    return <div className="flex h-full flex-col" />
  }

  return (
    <div className="relative flex h-full flex-col">
      <div className="flex-1 overflow-y-auto scroll-thin bg-plum-50/40 dark:bg-transparent">
        {/* EVENT HERO — this band is the page header */}
        <section className="border-b border-plum-200/60 bg-white dark:border-white/[0.08] dark:bg-white/[0.02]">
          <div className="mx-auto flex max-w-[760px] items-center gap-5 px-5 py-5">
            <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-plum-500 to-plum-800 text-[17px] font-bold text-white shadow-fluent-sm">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="truncate text-[20px] font-semibold tracking-tightish text-plum-900 dark:text-plum-50">
                  {row?.name ?? "Event"}
                </h1>
                {statusControl}
              </div>
              <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12.5px] text-plum-700/65 dark:text-plum-200/50">
                <span>{formatDateRange(row?.startsAt ?? null, row?.endsAt ?? null)}</span>
                {row?.venueName && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {[row.venueName, row.addressLocality].filter(Boolean).join(", ")}
                  </span>
                )}
                {countdownLabel(row?.startsAt ?? null) && (
                  <span className="font-semibold text-plum-800/80 dark:text-plum-100/70">
                    {countdownLabel(row?.startsAt ?? null)}
                  </span>
                )}
              </p>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-[760px] space-y-4 px-5 py-5">
          {/* THE FUNNEL — invited → confirmed → checked in, one hero. */}
          <FunnelHero
            stats={stats}
            orgSlug={orgSlug}
            eventSlug={eventSlug}
            loading={guestsQ.isLoading}
          />

          {/* Everything the funnel doesn't cover. Tickets get the double
              slot — it's the only number here anyone acts on during an event. */}
          <div className="grid grid-cols-1 gap-2.5 md:grid-cols-3">
            <TicketsStatCard
              open={openTickets.length}
              closed={tickets.length - openTickets.length}
              mine={myOpenTickets}
              orgSlug={orgSlug}
              eventSlug={eventSlug}
              loading={ticketsQ.isLoading}
            />
            <StatCard
              label="Waitlist"
              value={stats.waitlist}
              sub={stats.waitlist === 0 ? "No one waiting" : "Auto-promote on cancel"}
              icon={<Users className="h-[18px] w-[18px]" />}
              to="/$orgSlug/$eventSlug/people"
              view="waitlist"
              orgSlug={orgSlug}
              eventSlug={eventSlug}
              loading={guestsQ.isLoading}
            />
          </div>

          {/* GUEST REPORTS — open into slide-over panels */}
          <OverviewReports />

          {/* MAIN + ACTIVITY COLUMN */}
          <div className="grid gap-4">
            <div className="grid gap-4">
            {/* NEEDS ATTENTION */}
            <section className="rounded-2xl border border-plum-200/70 bg-white p-4 shadow-fluent-sm dark:border-white/[0.08] dark:bg-white/[0.03]">
              <div className="mb-2.5 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-rose-500" />
                <span className="text-[13px] font-semibold text-plum-900 dark:text-plum-50">Needs attention</span>
                {needsAttention.length > 0 && (
                  <span className="ml-auto rounded-full bg-rose-100 px-1.5 text-[10.5px] font-bold text-rose-600 dark:bg-rose-500/15 dark:text-rose-400">
                    {needsAttention.length}
                  </span>
                )}
              </div>
              {ticketsQ.isLoading && needsAttention.length === 0 ? (
                <div className="space-y-2">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="flex w-full items-center gap-2.5 rounded-xl border border-plum-200/70 p-2.5 dark:border-white/[0.08]"
                    >
                      <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-plum-100 dark:bg-white/10" />
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="h-3 w-2/5 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
                        <div className="h-2.5 w-1/4 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
                      </div>
                      <div className="h-3.5 w-12 shrink-0 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
                    </div>
                  ))}
                </div>
              ) : needsAttention.length === 0 ? (
                <EmptyLine loading={ticketsQ.isLoading} text="Nothing needs attention." />
              ) : (
                <div className="space-y-2">
                  {needsAttention.map((t) => {
                    const urgent = (t.priority ?? "normal") === "urgent"
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() =>
                          navigate({
                            to: "/$orgSlug/$eventSlug/t/$ticketId",
                            params: { orgSlug, eventSlug, ticketId: t.id },
                          })
                        }
                        className={`flex w-full items-center gap-2.5 rounded-xl border p-2.5 text-left transition hover:shadow-fluent-sm ${
                          urgent
                            ? "border-rose-300/60 ring-1 ring-rose-200/40 dark:border-rose-500/25 dark:ring-rose-500/10"
                            : "border-plum-200/70 hover:border-plum-300 dark:border-white/[0.08]"
                        }`}
                      >
                        <Avatar
                          name={ticketGuestName(t, guestNameById)}
                          kind="guest"
                          size={32}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-[13px] font-semibold text-plum-900 dark:text-plum-50">
                            {t.subject || "Untitled ticket"}
                          </div>
                          <div className="truncate text-[11.5px] text-plum-700/55 dark:text-plum-200/40">
                            {ticketGuestName(t, guestNameById)} · {relTime(t.modifiedAt ?? t.createdAt)}
                          </div>
                        </div>
                        <PriorityChip priority={t.priority} />
                      </button>
                    )
                  })}
                </div>
              )}
            </section>

            {/* UP NEXT */}
            <section className="rounded-2xl border border-plum-200/70 bg-white p-4 shadow-fluent-sm dark:border-white/[0.08] dark:bg-white/[0.03]">
              <div className="mb-2.5 flex items-center gap-2">
                <CalendarClock className="h-4 w-4 text-plum-700/65 dark:text-plum-200/55" />
                <span className="text-[13px] font-semibold text-plum-900 dark:text-plum-50">Up next</span>
                <Link
                  to="/$orgSlug/$eventSlug/schedule"
                  params={{ orgSlug, eventSlug }}
                  className="ml-auto text-[11px] font-semibold text-plum-700/70 transition hover:text-plum-900 dark:text-plum-200/55 dark:hover:text-plum-50"
                >
                  See all →
                </Link>
              </div>
              {agendaQ.isLoading && upNext.length === 0 ? (
                <div className="space-y-2">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="flex gap-3">
                      <div className="w-12 shrink-0 pt-2 text-right">
                        <div className="ml-auto h-2.5 w-9 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
                      </div>
                      <div className="min-w-0 flex-1 space-y-1.5 rounded-xl border border-plum-200/70 px-3 py-2.5 dark:border-white/10">
                        <div className="h-3 w-1/2 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
                        <div className="h-2.5 w-1/3 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : upNext.length === 0 ? (
                <EmptyLine loading={agendaQ.isLoading} text="Nothing scheduled yet." />
              ) : (
                /* Mirrors the guest record's Schedule tab: left time gutter +
                   a card row (name + location subline). */
                <div className="space-y-2">
                  {upNext.map((it) => (
                    <Link
                      key={it.id}
                      to="/$orgSlug/$eventSlug/schedule"
                      params={{ orgSlug, eventSlug }}
                      className="flex gap-3 text-left"
                    >
                      <div className="w-12 shrink-0 pt-2 text-right text-[11px] font-semibold lowercase tabular-nums text-plum-700/50 dark:text-plum-200/40">
                        {formatItemTime(it.startsAt)}
                      </div>
                      <div className="min-w-0 flex-1 rounded-xl border border-plum-200/70 bg-white px-3 py-2.5 shadow-fluent-sm transition hover:border-plum-300 hover:shadow-fluent dark:border-white/10 dark:bg-white/[0.03]">
                        <span className="block truncate text-[13px] font-semibold text-plum-900 dark:text-plum-50">
                          {it.name}
                        </span>
                        {it.venueName && (
                          <div className="mt-0.5 flex items-center gap-1 text-[11px] text-plum-700/55 dark:text-plum-200/40">
                            <MapPin className="h-3 w-3 shrink-0" />
                            <span className="truncate">{it.venueName}</span>
                          </div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </section>
            </div>
            <ActivityColumn orgSlug={orgSlug} eventSlug={eventSlug} />
          </div>
        </div>
      </div>
    </div>
  )
}

// ── stat card ────────────────────────────────────────────────────────────────
function StatCard({
  label,
  value,
  sub,
  icon,
  to,
  orgSlug,
  eventSlug,
  tone,
  loading,
  view,
}: {
  label: string
  value: number
  sub: string
  icon: React.ReactNode
  to: "/$orgSlug/$eventSlug/people" | "/$orgSlug/$eventSlug/tickets"
  orgSlug: string
  eventSlug: string
  tone?: "warning"
  loading?: boolean
  /** Saved view to land the People table on (e.g. "waitlist"). */
  view?: string
}) {
  return (
    <Link
      to={to}
      params={{ orgSlug, eventSlug }}
      search={view ? { view } : {}}
      className={`group block rounded-xl border bg-white p-3.5 shadow-fluent-sm transition hover:shadow-fluent dark:bg-white/[0.03] ${
        tone === "warning"
          ? "border-amber-300/70 hover:border-amber-400 dark:border-amber-500/25"
          : "border-plum-200/70 hover:border-plum-300 dark:border-white/[0.08]"
      }`}
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-[0.06em] text-plum-700/55 dark:text-plum-200/40">
          {label}
        </span>
        <span
          className={`grid h-7 w-7 place-items-center rounded-lg ${
            tone === "warning"
              ? "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400"
              : "bg-plum-100 text-plum-700 dark:bg-plum-700/30 dark:text-plum-300"
          }`}
        >
          {icon}
        </span>
      </div>
      {loading ? (
        <div className="h-7 w-12 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
      ) : (
        <div className="text-[26px] font-semibold leading-none tracking-tightish tabular-nums text-plum-900 dark:text-plum-50">
          {value}
        </div>
      )}
      <div className="mt-1 truncate text-[11px] text-plum-700/55 dark:text-plum-200/40">{sub}</div>
    </Link>
  )
}


// ── small bits ───────────────────────────────────────────────────────────────
function PriorityChip({ priority }: { priority: string | null }) {
  const p = priority ?? "normal"
  const cls: Record<string, string> = {
    urgent: "text-rose-600 dark:text-rose-400 bg-rose-100 dark:bg-rose-500/15",
    high: "text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-500/15",
    normal: "text-plum-700/70 dark:text-plum-200/55 bg-plum-100 dark:bg-white/10",
    low: "text-plum-600/60 dark:text-plum-300/45 bg-plum-50 dark:bg-white/5",
  }
  return (
    <span className={`shrink-0 rounded px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide ${cls[p] ?? cls.normal}`}>
      {p}
    </span>
  )
}

function EmptyLine({ loading, text }: { loading: boolean; text: string }) {
  return (
    <p className="py-2 text-[12.5px] text-plum-700/45 dark:text-plum-200/35">
      {loading ? "Loading…" : text}
    </p>
  )
}

// ── date/time helpers ────────────────────────────────────────────────────────
function deriveInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "—"
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function cap(s: string): string {
  return s.replace(/^./, (c) => c.toUpperCase())
}

// "May 12–15, 2027" — the month and year are said once when they're shared,
// so the range reads as one span. Falls back to spelling both sides out when
// the months or years differ ("Dec 30, 2027 – Jan 2, 2028").
function formatDateRange(start: string | null, end: string | null): string {
  if (!start) return "Dates TBC"
  try {
    const s = new Date(start)
    const full = (d: Date) =>
      d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    if (!end) return full(s)
    const e = new Date(end)
    const sameYear = s.getFullYear() === e.getFullYear()
    const sameMonth = sameYear && s.getMonth() === e.getMonth()
    if (sameMonth) {
      if (s.getDate() === e.getDate()) return full(s)
      const month = s.toLocaleDateString(undefined, { month: "short" })
      return `${month} ${s.getDate()}–${e.getDate()}, ${s.getFullYear()}`
    }
    if (sameYear) {
      const md = (d: Date) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
      return `${md(s)} – ${md(e)}, ${s.getFullYear()}`
    }
    return `${full(s)} – ${full(e)}`
  } catch {
    return start
  }
}

function countdownLabel(start: string | null): string | null {
  if (!start) return null
  const ms = parseMs(start)
  if (Number.isNaN(ms)) return null
  const days = Math.ceil((ms - Date.now()) / 86_400_000)
  if (days > 1) return `${days} days to go`
  if (days === 1) return "Tomorrow"
  if (days === 0) return "Today"
  return "In progress"
}

function formatItemTime(naive: string): string {
  try {
    const time = naive.slice(11, 16)
    if (!time) return naive
    const [h, m] = time.split(":").map(Number)
    const ampm = h >= 12 ? "PM" : "AM"
    const h12 = ((h + 11) % 12) + 1
    return `${h12}:${m.toString().padStart(2, "0")} ${ampm}`
  } catch {
    return naive
  }
}

function parseMs(iso: string): number {
  return Date.parse(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`)
}

function relTime(iso: string): string {
  const t = parseMs(iso)
  if (Number.isNaN(t)) return ""
  const m = Math.round((Date.now() - t) / 60000)
  if (m < 1) return "just now"
  if (m < 60) return `${m}m ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.round(h / 24)}d ago`
}
